import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Smartphone } from "lucide-react";
import { pushSupported, enablePush, disablePush, isPushSubscribed } from "../lib/push";

const isIOS = () => typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);

export const NotificationsToggle = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const supported = pushSupported();
  const iosNeedsInstall = isIOS() && !isStandalone();

  useEffect(() => {
    (async () => { setOn(await isPushSubscribed()); })();
  }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (on) {
        await disablePush();
        setOn(false);
      } else {
        const res = await enablePush();
        setPerm(typeof Notification !== "undefined" ? Notification.permission : "denied");
        setOn(!!res.ok);
      }
    } catch (e) { /* noop */ }
    setBusy(false);
  };

  // iPhone/iPad in a browser tab: push only works once installed to the Home Screen.
  if (iosNeedsInstall) {
    return (
      <div className="w-full border border-white/15 px-5 py-4 font-body" data-testid="notifications-toggle">
        <div className="flex items-start gap-3">
          <Smartphone size={18} className="text-[#14A89A] shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-sm">Turn on notifications on iPhone</p>
            <p className="text-zinc-500 text-xs mt-1 leading-relaxed">Tap the <span className="text-white">Share</span> icon in Safari, choose <span className="text-white">"Add to Home Screen,"</span> then open Sovereign Signal from that icon and toggle notifications on here.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!supported) {
    return (
      <div className="w-full border border-white/15 px-5 py-4 font-body" data-testid="notifications-toggle">
        <div className="flex items-center gap-2 text-zinc-500 text-sm"><BellOff size={16} /> Notifications aren't supported in this browser.</div>
      </div>
    );
  }

  const blocked = perm === "denied" && !on;

  return (
    <div className="w-full border border-white/15 font-body" data-testid="notifications-toggle">
      <button
        onClick={toggle}
        disabled={busy || blocked}
        data-testid="notifications-toggle-button"
        aria-pressed={on}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5 transition-colors disabled:cursor-default"
      >
        <span className={`shrink-0 ${on ? "text-[#14A89A]" : "text-zinc-500"}`}>
          {busy ? <Loader2 size={18} className="animate-spin" /> : on ? <Bell size={18} /> : <BellOff size={18} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-white text-sm">Notifications</span>
          <span className="block text-zinc-500 text-xs mt-0.5">
            {on ? "On: you'll be signalled when a sovereign friend reaches out." : "Off: tap to be notified when a sovereign friend reaches out."}
          </span>
        </span>
        {/* Custom toggle switch */}
        <span
          data-testid="notifications-switch"
          data-state={on ? "on" : "off"}
          className={`shrink-0 w-11 h-6 rounded-full relative transition-colors ${on ? "bg-[#14A89A]" : "bg-white/15"} ${busy || blocked ? "opacity-50" : ""}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0"}`} />
        </span>
      </button>
      {blocked && (
        <p className="px-5 pb-4 -mt-1 text-zinc-500 text-xs leading-relaxed" data-testid="notifications-blocked-note">
          Notifications are blocked for Sovereign Signal. Enable them in your browser or device settings, then reload to switch them on.
        </p>
      )}
    </div>
  );
};
